import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Zap } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { gruppoColor } from "@/lib/race";

export default function EventsTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ nome: "", descrizione: "", punti: "", icona: "" });
  const [search, setSearch] = useState("");
  const [pilotId, setPilotId] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [manche, setManche] = useState("Fuori Gara");
  const [note, setNote] = useState("");

  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events")).data,
  });
  const { data: pilots = [] } = useQuery({
    queryKey: ["pilots", "", "", ""],
    queryFn: async () => (await api.get("/pilots")).data,
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ["pilot-events"],
    queryFn: async () => (await api.get("/pilot-events")).data,
  });

  const filteredPilots = pilots.filter((p) =>
    `${p.nome} ${p.cognome} ${p.vettura}`.toLowerCase().includes(search.toLowerCase())
  );

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/events", { ...form, punti: parseFloat(form.punti) });
      toast.success("Evento creato");
      setForm({ nome: "", descrizione: "", punti: "", icona: "" });
      qc.invalidateQueries({ queryKey: ["events"] });
    } catch (err) {
      toast.error(errMsg(err, "Creazione fallita"));
    }
  };

  const deleteEvent = async (id) => {
    await api.delete(`/admin/events/${id}`);
    toast.success("Evento eliminato");
    qc.invalidateQueries({ queryKey: ["events"] });
    qc.invalidateQueries({ queryKey: ["pilot-events"] });
  };

  const toggleEvent = (id) =>
    setSelectedEvents((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const assign = async () => {
    if (!pilotId || selectedEvents.length === 0) {
      toast.error("Seleziona un pilota e almeno un evento");
      return;
    }
    try {
      for (const eventId of selectedEvents) {
        await api.post("/admin/pilot-events", { pilot_id: pilotId, event_id: eventId, manche, note });
      }
      toast.success("Eventi registrati e punteggi ricalcolati");
      setSelectedEvents([]);
      setNote("");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e, "Assegnazione fallita"));
    }
  };

  const removeAssignment = async (id) => {
    await api.delete(`/admin/pilot-events/${id}`);
    toast.success("Assegnazione rimossa, punteggi ricalcolati");
    qc.invalidateQueries();
  };

  const PRESETS = [
    { nome: "Bonus Intervista/Premiazione", descrizione: "Cita Dinamika, FantasyRace, ASD Monti Iblei o pronuncia 'Cichirillì'", punti: 5, icona: "🎤" },
    { nome: "Bonus Tag Social", descrizione: "Tagga Dinamika, FantasyRace o ASD Monti Iblei sui social", punti: 3, icona: "📲" },
    { nome: "Bonus Ricorso Vinto", descrizione: "Ricorso ufficiale vinto entro la domenica di gara", punti: 20, icona: "⚖️" },
    { nome: "Bonus Sorpasso Effettuato", descrizione: "Sorpasso effettuato in pista durante la manche", punti: 10, icona: "🔥" },
    { nome: "Malus Ricorso Perso", descrizione: "Ricorso ufficiale perso entro la domenica di gara", punti: -20, icona: "📉" },
    { nome: "Malus Sorpasso Subito", descrizione: "Sorpasso subito in pista durante la manche", punti: -10, icona: "🐌" },
  ];

  const loadPresets = async () => {
    const missing = PRESETS.filter((p) => !events.some((e) => e.nome === p.nome));
    if (!missing.length) {
      toast.info("Preset già tutti presenti");
      return;
    }
    for (const p of missing) {
      await api.post("/admin/events", p);
    }
    toast.success(`${missing.length} eventi predefiniti caricati`);
    qc.invalidateQueries({ queryKey: ["events"] });
  };

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
      <div className="space-y-4">
        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest mb-4">
            Configuratore Bonus/Malus
          </h3>
          <form onSubmit={createEvent} className="space-y-3">
            <input data-testid="event-form-nome" className={inputCls} placeholder="Nome evento (es. Testa a Coda)" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
            <input data-testid="event-form-descrizione" className={inputCls} placeholder="Descrizione breve" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="event-form-punti" type="number" step="0.5" className={inputCls} placeholder="Punti (+5 / -10)" value={form.punti} onChange={(e) => setForm({ ...form, punti: e.target.value })} required />
              <input data-testid="event-form-icona" className={inputCls} placeholder="Icona (es. ⚡)" value={form.icona} onChange={(e) => setForm({ ...form, icona: e.target.value })} />
            </div>
            <button data-testid="event-form-submit" className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2 font-body-sport font-bold text-xs tracking-widest text-white uppercase">
              <Plus className="w-4 h-4" /> Crea Evento
            </button>
          </form>
          <div className="mt-4 pt-4 border-t border-[#3F3F4C]">
            <p className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase mb-2">
              Preset ufficiali di gara
            </p>
            <button
              data-testid="load-preset-events"
              onClick={loadPresets}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-4 py-2.5 font-body-sport font-bold text-xs tracking-widest uppercase"
            >
              <Zap className="w-4 h-4" /> Carica Bonus/Malus Predefiniti
            </button>
            <p className="text-[10px] text-gray-500 font-body-sport mt-2 leading-relaxed">
              +5 Intervista/Premiazione (citazione Dinamika/FantasyRace/Cichirillì) • +3 Tag Social • +20 Ricorso Vinto •
              +10 Sorpasso Effettuato • -20 Ricorso Perso • -10 Sorpasso Subito
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest mb-3">
            Eventi Disponibili
          </h3>
          <div className="space-y-2">
            {events.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-body-sport font-semibold text-white truncate">
                    {ev.icona} {ev.nome}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">{ev.descrizione}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-time text-sm font-bold ${ev.punti >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                    {ev.punti >= 0 ? `+${ev.punti}` : ev.punti}
                  </span>
                  <button data-testid={`event-delete-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="text-gray-500 hover:text-[#FF1E27] transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {events.length === 0 && <p className="text-xs text-gray-500">Nessun evento configurato.</p>}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest">
            Assegnazione Rapida ai Piloti
          </h3>
          <input
            data-testid="assign-search-input"
            className={inputCls}
            placeholder="Cerca pilota..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
            {filteredPilots.map((p) => (
              <button
                key={p.id}
                data-testid={`assign-pilot-${p.id}`}
                onClick={() => setPilotId(p.id)}
                className={`w-full flex items-center gap-2 rounded-lg border px-3 py-1.5 text-left transition-colors ${
                  pilotId === p.id ? "border-[#FF1E27] bg-[#FF1E27]/10" : "border-[#3F3F4C] hover:border-[#FF1E27]/50"
                }`}
              >
                <span className="font-time text-xs font-bold" style={{ color: gruppoColor(p.gruppo) }}>{p.numero}</span>
                <span className="font-body-sport text-sm text-white truncate">{p.nome} {p.cognome}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {events.map((ev) => (
              <button
                key={ev.id}
                data-testid={`assign-event-${ev.id}`}
                onClick={() => toggleEvent(ev.id)}
                className={`rounded-full border px-2.5 py-1 font-body-sport text-[11px] font-bold transition-colors ${
                  selectedEvents.includes(ev.id)
                    ? "border-[#FFB703] bg-[#FFB703]/15 text-[#FFB703]"
                    : "border-[#3F3F4C] text-gray-400 hover:border-[#FFB703]/60"
                }`}
              >
                {ev.icona} {ev.nome} ({ev.punti >= 0 ? `+${ev.punti}` : ev.punti})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <select
              data-testid="assign-manche-select"
              className={inputCls}
              value={manche}
              onChange={(e) => setManche(e.target.value)}
            >
              <option>Manche 1</option>
              <option>Manche 2</option>
              <option>Fuori Gara</option>
            </select>
            <input
              data-testid="assign-note-input"
              className={inputCls}
              placeholder="Note (opzionale)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <button
            data-testid="assign-submit-button"
            onClick={assign}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#FFB703] hover:bg-[#F5A623] transition-colors py-2.5 font-racing font-bold text-xs tracking-widest text-black uppercase"
          >
            <Zap className="w-4 h-4" /> Registra Evento e Ricalcola Punteggi
          </button>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest mb-3">
            Eventi Assegnati
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {assignments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs font-body-sport text-white truncate">
                    <span className="font-bold">{a.pilota.cognome}</span> — {a.evento.icona} {a.evento.nome}
                  </p>
                  <p className="text-[10px] text-gray-500">{a.manche}{a.note ? ` • ${a.note}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-time text-xs font-bold ${a.evento.punti >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                    {a.evento.punti >= 0 ? `+${a.evento.punti}` : a.evento.punti}
                  </span>
                  <button data-testid={`assignment-delete-${a.id}`} onClick={() => removeAssignment(a.id)} className="text-gray-500 hover:text-[#FF1E27] transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {assignments.length === 0 && <p className="text-xs text-gray-500">Nessuna assegnazione registrata.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
